import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearchPlus, faSearchMinus } from '@fortawesome/free-solid-svg-icons';

/**
 * Component for embossed item controls
 */
const EmbossedItemEditor = ({ item, index, updateItem, deleteItem, setActiveItem, isActive, gridSize }) => {
    const [showZoomedThumbnail, setShowZoomedThumbnail] = useState(false);
    
    // Parse grid dimensions for scaling guidance
    const getGridDimensions = () => {
        if (!gridSize || typeof gridSize !== 'string') return { cols: 6, rows: 6 }; // Default
        
        const parts = gridSize.split('x');
        if (parts.length !== 2) return { cols: 6, rows: 6 }; // Default if invalid format
        
        const cols = parseInt(parts[0], 10) || 6;
        const rows = parseInt(parts[1], 10) || 6;
        return { cols, rows };
    };
    
    const { cols, rows } = getGridDimensions();
    
    // Calculate recommended scale range based on grid size
    const getScaleRange = () => {
        // Always return the same scale range regardless of grid size
        return { min: 0.5, max: 5.0, default: 2.0 };
    };
    
    const scaleRange = getScaleRange();
    
    // Toggle thumbnail zoom
    const toggleZoom = (e) => {
        e.stopPropagation(); // Prevent triggering setActiveItem
        setShowZoomedThumbnail(!showZoomedThumbnail);
    };

    // Determine grid size message to display
    const getGridSizeMessage = () => {
        // Remove the dynamic sizing message
        return "Fixed emboss sizing applied";
    };
    
    return (
        <div className={`p-3 border mb-2 rounded ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
            <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Item {index + 1}</h4>
                <button 
                    onClick={() => deleteItem(index)}
                    className="text-red-500 hover:text-red-700"
                    aria-label="Delete item"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            
            {/* Larger thumbnail container with proper aspect ratio preservation */}
            <div 
                className="flex items-center justify-center mb-3 p-3 bg-gray-100 cursor-pointer border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                onClick={() => setActiveItem(index)}
            >
                <img 
                    src={item.dataUrl} 
                    alt={`Item ${index + 1}`} 
                    className="max-w-full h-auto max-h-32 object-contain"
                />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="block text-xs text-gray-600 mb-1">
                        Size <span className="text-blue-600">{item.scale?.toFixed(1)}</span>
                    </label>
                    <input
                        type="range"
                        min={scaleRange.min}
                        max={scaleRange.max}
                        step="0.1"
                        value={item.scale || scaleRange.default}
                        onChange={(e) => updateItem(index, { scale: parseFloat(e.target.value) })}
                        className="w-full"
                    />
                    <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">{scaleRange.min.toFixed(1)}</span>
                        <span className="text-xs text-gray-500">{scaleRange.max.toFixed(1)}</span>
                    </div>
                </div>
                <div>
                    <label className="block text-xs text-gray-600 mb-1">
                        Rotation <span className="text-blue-600">{item.rotation || 0}°</span>
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="360"
                        value={item.rotation || 0}
                        onChange={(e) => updateItem(index, { rotation: parseInt(e.target.value) })}
                        className="w-full"
                    />
                </div>
            </div>
            
            {/* Show grid size guidance */}
            <div className="mt-1 text-xs text-gray-500">
                <p>{getGridSizeMessage()}</p>
                <p className="text-blue-500 mt-1">Recommended size: 2.0</p>
            </div>
            
            <div className="mt-2">
                <label className="block text-xs text-gray-600 mb-1">Position</label>
                
                {/* Position preview - helps visualize where item will be placed */}
                <div className="w-full h-[60px] bg-gray-100 border border-gray-300 rounded-md mb-2 relative">
                    {/* Position indicator dot */}
                    <div 
                        className="absolute w-3 h-3 bg-blue-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 z-10"
                        style={{ 
                            left: `${item.x}%`, 
                            top: `${item.y}%`,
                            boxShadow: '0 0 0 2px white'
                        }}
                    ></div>
                    
                    {/* Guide lines */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute left-1/2 top-0 bottom-0 border-l border-dashed border-gray-400 opacity-30"></div>
                        <div className="absolute top-1/2 left-0 right-0 border-t border-dashed border-gray-400 opacity-30"></div>
                    </div>
                </div>

                {/* X position slider */}
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs w-5">X:</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={item.x || 50}
                        onChange={(e) => updateItem(index, { x: parseInt(e.target.value) })}
                        className="flex-grow"
                    />
                    <input
                        type="number"
                        min="0"
                        max="100"
                        value={item.x || 50}
                        onChange={(e) => updateItem(index, { x: parseInt(e.target.value) })}
                        className="w-14 p-1 text-sm border border-gray-300 rounded"
                    />
                </div>
                
                {/* Y position slider */}
                <div className="flex items-center gap-2">
                    <span className="text-xs w-5">Y:</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={item.y || 50}
                        onChange={(e) => updateItem(index, { y: parseInt(e.target.value) })}
                        className="flex-grow"
                    />
                    <input
                        type="number"
                        min="0"
                        max="100"
                        value={item.y || 50}
                        onChange={(e) => updateItem(index, { y: parseInt(e.target.value) })}
                        className="w-14 p-1 text-sm border border-gray-300 rounded"
                    />
                </div>
            </div>
        </div>
    );
};

export default EmbossedItemEditor;
